import { Controller, useForm } from 'react-hook-form';
import Input from 'src/components/Input/Input';
import React from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { booleanScheme, stringScheme } from 'src/utils/validation-schemes';
import { AnalyticsDateRangePopup } from 'src/pages/AnalyticsPage/AnalyticsDateRangePopup/AnalyticsDateRangePopup';
import moment from 'moment';
import type {
  CustomDateRangeValues,
  DateRangeItem,
} from 'src/pages/AnalyticsPage/AnalyticsCustomer/AnalyticsCoCustomer.types';

const FORMAT = 'YYYY-MM-DD';
const initialCustomDate = {
  from: moment().toDate(),
  to: moment().toDate(),
};

// A date range choice; picking 'Custom' opens the custom range popup instead.
type DateRangeOption = Pick<DateRangeItem, 'label' | 'disabled' | 'default'>;

interface AnalyticsDateProps {
  onDateChange?: (range: DateRangeItem) => void;
  selectOptions: DateRangeOption[];
}

export const AnalyticsDate = ({ onDateChange, selectOptions }: AnalyticsDateProps) => {
  const [customDateDefault, setCustomDateDefault] =
    React.useState<CustomDateRangeValues['date']>(initialCustomDate);
  const [openCustomPopup, setOpenCustomPopup] = React.useState(false);
  const { control, trigger } = useForm({
    defaultValues: {
      date: selectOptions[0],
    },
    resolver: yupResolver(
      object({
        date: object({
          label: stringScheme({ required: true }),
          disabled: booleanScheme(),
        }),
      }),
    ),
  });

  const handleDateChange = (e: DateRangeOption) => {
    if (e.label === 'Custom') {
      return setOpenCustomPopup(true);
    }
    setCustomDateDefault(initialCustomDate);
    e.label && onDateChange?.(e);
  };

  const submitCustomDateRange = ({ date }: CustomDateRangeValues) => {
    const start_time = moment(date.from).startOf('day').format(FORMAT);
    const end_time = moment(date.to).endOf('day').format(FORMAT);

    setCustomDateDefault(date);
    onDateChange?.({ label: 'Custom', start_time, end_time });
  };

  return (
    <div className={'input-container md:min-w-52 text-[.88rem] min-w-[140px]'}>
      <Controller
        control={control}
        name="date"
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Input
            type={'select'}
            selectOptions={selectOptions}
            invalidMessage={error?.message}
            value={value}
            isValid={!error?.message}
            onChange={(e) => {
              onChange(e);
              handleDateChange(e);
            }}
            onBlur={() => trigger('date')}
          />
        )}
      />
      <AnalyticsDateRangePopup
        theme="agent"
        open={openCustomPopup}
        setOpen={setOpenCustomPopup}
        dateDefault={customDateDefault}
        submitDateFilter={submitCustomDateRange}
      />
    </div>
  );
};
